import { useMemo, useState } from 'react';

import { AlertTriangle, Repeat, UserCheck } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { SolicitarSubstituicaoDialog } from '@/modulos/portal-credenciado/comum/SolicitarSubstituicaoDialog';
import { PortalAccessGuard } from '@/modulos/portal-credenciado/comum/PortalAccessGuard';
import {
  OPCOES_DE_VENCIMENTO,
  alertaDeValidade,
  presetValido,
} from '@/modulos/portal-credenciado/comum/vencimento';
import { CopyableCpfCnpj } from '@/ui/copyable';
import { DataTable } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { PageHeader } from '@/ui/PageHeader';
import { StatusBadge } from '@/ui/StatusBadge';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';

import { useCondutores } from './dados';
import type { Condutor, FiltroDeCondutores } from './contrato';

const TAMANHO_DA_PAGINA = 10;

/**
 * Módulo, e não dentro da tela: `useTableViewPreferences` pede lista estável.
 * O nome identifica a linha e não se oculta; a CNH explica o filtro de
 * vencimento, e o botão de personalizar avisa se ela estiver oculta com ele ativo.
 */
const COLUNAS: TableViewColumn<Condutor>[] = [
  {
    id: 'nome',
    header: 'Nome',
    // O nome é o Link para o detalhe: o botão de copiar o CPF é um controle
    // focável dentro da linha, então a linha não pode ser também um botão
    // (`nested-interactive`, regras de interface 5 e 9). O teclado abre o
    // detalhe pelo Link; o mouse continua abrindo pela linha inteira. Mesmo
    // desenho de Solicitações.
    cell: (condutor) => (
      <Link
        to={`/credenciado/condutores/${condutor.slug}`}
        className="focus-visible:ring-ring rounded hover:underline focus:outline-hidden focus-visible:ring-2"
      >
        {condutor.nome}
      </Link>
    ),
    sortKey: 'nome',
    isCritical: true,
  },
  {
    id: 'cpf',
    header: 'CPF',
    cell: (condutor) => <CopyableCpfCnpj value={condutor.cpf} className="whitespace-nowrap" />,
  },
  {
    id: 'cnh',
    header: 'CNH',
    linkedFilterKeys: ['cnh'],
    cell: (condutor) => {
      const alerta = alertaDeValidade(condutor.cnh_validade);
      return (
        <div className="flex items-center gap-1.5 text-xs">
          {condutor.cnh_categoria && (
            <Badge variant="outline" className="text-[10px]">
              {condutor.cnh_categoria}
            </Badge>
          )}
          {alerta && (
            <AlertTriangle
              className={`h-3.5 w-3.5 ${alerta === 'vencido' ? 'text-destructive-text' : 'text-warning'}`}
              role="img"
              aria-label={alerta === 'vencido' ? 'CNH vencida' : 'CNH próxima do vencimento'}
            />
          )}
        </div>
      );
    },
  },
  {
    id: 'cursos',
    header: 'Cursos',
    cell: (condutor) => (
      <div className="flex flex-wrap gap-1">
        {condutor.curso_primeiro_socorros && (
          <Badge variant="outline" className="text-[10px]">
            1ºs Socorros
          </Badge>
        )}
        {condutor.curso_transporte_escolar && (
          <Badge variant="outline" className="text-[10px]">
            Transp. Escolar
          </Badge>
        )}
      </div>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: (condutor) => <StatusBadge status={condutor.situacao} label={condutor.situacao} />,
    sortKey: 'situacao',
    isStatusColumn: true,
  },
];

const TITULO = 'Meus Condutores';
const DESCRICAO = 'Condutores vinculados ao seu credenciamento';

/**
 * `/credenciado/condutores` — "Meus Condutores", fiel a
 * `pages/portal/PortalCondutores.tsx` do protótipo: mesmo cabeçalho, o botão
 * "Solicitar substituição" em linha própria, os filtros Buscar e CNH
 * (vencimento), e as cinco colunas Nome, CPF, CNH, Cursos e Status.
 *
 * Dado mockado — ver `dados.ts` e `comum/mock.ts` para a justificativa.
 * Exclusiva de credenciado coletivo (PJ ou Cooperativa), pela guarda
 * `requirePJ`, igual ao protótipo. Credenciado Pessoa Física não tem
 * condutor contratado à parte: ele mesmo dirige.
 *
 * Uma diferença deliberada do protótipo: o estado vazio de filtro diz que o
 * filtro não achou ninguém. O protótipo repete "Nenhum condutor cadastrado"
 * mesmo com condutores na lista, o que afirma um fato falso sobre o cadastro.
 *
 * `embutida`: a tela entra como aba de Frota & Equipe, que já tem o `h1` da
 * página; o título desta vira `h2` (`PageHeader nivel={2}`). A rota própria
 * não passa a prop e continua com o `h1`.
 */
export function CondutoresPage({ embutida = false }: { embutida?: boolean }) {
  return (
    <PortalAccessGuard regra="requirePJ">
      <ListaDeCondutores embutida={embutida} />
    </PortalAccessGuard>
  );
}

function ListaDeCondutores({ embutida }: { embutida: boolean }) {
  const navigate = useNavigate();

  const [busca, setBusca] = useState('');
  const [cnh, setCnh] = useState('');
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(TAMANHO_DA_PAGINA);
  const [substituicaoAberta, setSubstituicaoAberta] = useState(false);

  const filtro: FiltroDeCondutores = useMemo(
    () => ({
      pagina,
      tamanho,
      busca: busca || undefined,
      cnh: presetValido(cnh),
    }),
    [pagina, tamanho, busca, cnh],
  );

  const { data, isLoading, isError, refetch } = useCondutores(filtro);

  const temFiltro = Boolean(busca || cnh);

  const campos: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Buscar',
      type: 'search',
      placeholder: 'Buscar por nome ou CPF...',
      ariaLabel: 'Buscar condutores por nome ou CPF',
      flex: true,
    },
    {
      key: 'cnh',
      label: 'CNH',
      type: 'select',
      options: [...OPCOES_DE_VENCIMENTO],
      placeholder: 'Vencimento',
      ariaLabel: 'Vencimento da CNH',
      width: 'w-full sm:w-[180px]',
    },
  ];

  const filterValues = useMemo(() => ({ cnh }), [cnh]);

  const tableView = useTableViewPreferences({
    contextKey: 'credenciado:condutores:list',
    columns: COLUNAS,
    filterValues,
  });

  return (
    <div className="space-y-4">
      <PageHeader nivel={embutida ? 2 : 1} title={TITULO} description={DESCRICAO} />

      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => setSubstituicaoAberta(true)}
        >
          <Repeat className="h-4 w-4" aria-hidden="true" /> Solicitar substituição
        </Button>
        <SolicitarSubstituicaoDialog
          open={substituicaoAberta}
          onOpenChange={setSubstituicaoAberta}
        />
      </div>

      <FilterBar
        fields={campos}
        values={{ busca, cnh }}
        onChange={(chave, valor) => {
          if (chave === 'busca') setBusca(valor);
          if (chave === 'cnh') setCnh(valor);
          setPagina(1);
        }}
        onClear={() => {
          setBusca('');
          setCnh('');
          setPagina(1);
        }}
      />

      {isError ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar seus condutores agora. Tente novamente em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : (
        <DataTable
          columns={tableView.orderedColumns}
          personalizacao={tableView}
          data={data?.itens ?? []}
          isLoading={isLoading}
          total={data?.total}
          page={pagina}
          perPage={tamanho}
          onPageChange={setPagina}
          onPerPageChange={(novoTamanho) => {
            setTamanho(novoTamanho);
            setPagina(1);
          }}
          onRowClick={(condutor) => navigate(`/credenciado/condutores/${condutor.slug}`)}
          linhaComElementoInterativo
          emptyIcon={UserCheck}
          emptyTitle={temFiltro ? 'Nenhum condutor encontrado' : 'Nenhum condutor cadastrado'}
          emptyDescription={
            temFiltro
              ? 'Nenhum condutor atende a esses filtros. Tente afrouxar a busca.'
              : 'Você ainda não possui condutores vinculados ao seu credenciamento.'
          }
          minWidth="640px"
          ariaLabel="Lista de condutores"
        />
      )}
    </div>
  );
}

export default CondutoresPage;
